import {Inject, Injectable, Optional, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {CacheTagService} from './cache-tag.service';
import {environment} from "../../../environments/environment";
import {NetworkStatusService} from "./network-status.service";

export interface CacheEntry<T = unknown> {
  body: T;
  status: number;
  statusText: string;
  headers?: {[key: string]: string};
  url: string;
  updatedAt: number;
  ttlMs: number;
}

@Injectable({providedIn: 'root'})
export class CacheStoreService {
  private readonly dbName = 'wtg-cache';
  private readonly storeName = 'responses';
  private readonly lastActiveKey = 'cache.lastActiveAt';
  private readonly maxInactiveMs = 24 * 60 * 60 * 1000;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private useLocalStorage = false;
  private readonly enabled = environment.httpCacheEnabled;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() private cacheTags?: CacheTagService,
    private readonly networkStatus?: NetworkStatusService
  ) {}

  async init(): Promise<void> {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    try {
      await this.openDb();
    } catch {
      this.useLocalStorage = true;
    }
    if (this.shouldFlush()) {
      await this.clear();
    }
    this.touch();
  }

  touch(): void {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    localStorage.setItem(this.lastActiveKey, String(Date.now()));
  }

  isOffline(): boolean {
    if (!this.enabled || !this.isBrowser()) {
      return false;
    }
    if (this.networkStatus) {
      return this.networkStatus.isOffline();
    }
    return !navigator.onLine;
  }

  async get<T>(key: string): Promise<CacheEntry<T> | null> {
    if (!this.enabled || !this.isBrowser()) {
      return null;
    }
    if (this.useLocalStorage) {
      return this.getFromLocalStorage<T>(key);
    }
    const db = await this.ensureDb();
    if (!db) {
      return this.getFromLocalStorage<T>(key);
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async set<T>(key: string, entry: CacheEntry<T>): Promise<void> {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    if (this.useLocalStorage) {
      this.setInLocalStorage(key, entry);
      return;
    }
    const db = await this.ensureDb();
    if (!db) {
      this.setInLocalStorage(key, entry);
      return;
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.put(entry, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async deleteByPrefix(prefix: string): Promise<void> {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    if (this.useLocalStorage) {
      this.deleteFromLocalStorage(prefix);
      return;
    }
    const db = await this.ensureDb();
    if (!db) {
      this.deleteFromLocalStorage(prefix);
      return;
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const key = String(cursor.key);
        if (key.includes(prefix)) {
          cursor.delete();
          this.cacheTags?.removeKey(key);
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deleteByKey(key: string): Promise<void> {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    if (this.useLocalStorage) {
      localStorage.removeItem(this.localStorageKey(key));
      this.cacheTags?.removeKey(key);
      return;
    }
    const db = await this.ensureDb();
    if (!db) {
      localStorage.removeItem(this.localStorageKey(key));
      this.cacheTags?.removeKey(key);
      return;
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.delete(key);
      request.onsuccess = () => {
        this.cacheTags?.removeKey(key);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clear(): Promise<void> {
    if (!this.enabled || !this.isBrowser()) {
      return;
    }
    if (this.useLocalStorage) {
      this.clearLocalStorage();
      this.cacheTags?.clearAll();
      return;
    }
    const db = await this.ensureDb();
    if (!db) {
      this.clearLocalStorage();
      this.cacheTags?.clearAll();
      return;
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const request = store.clear();
      request.onsuccess = () => {
        this.cacheTags?.clearAll();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  private shouldFlush(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const lastActive = Number(localStorage.getItem(this.lastActiveKey) || 0);
    return lastActive > 0 && Date.now() - lastActive > this.maxInactiveMs;
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return this.dbPromise;
  }

  private async ensureDb(): Promise<IDBDatabase | null> {
    try {
      return await this.openDb();
    } catch {
      this.useLocalStorage = true;
      return null;
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getFromLocalStorage<T>(key: string): CacheEntry<T> | null {
    const raw = localStorage.getItem(this.localStorageKey(key));
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as CacheEntry<T>;
    } catch {
      return null;
    }
  }

  private setInLocalStorage<T>(key: string, entry: CacheEntry<T>): void {
    try {
      localStorage.setItem(this.localStorageKey(key), JSON.stringify(entry));
    } catch {
      // ignore quota errors
    }
  }

  private deleteFromLocalStorage(prefix: string): void {
    const match = this.localStorageKey(prefix);
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(match)) {
        localStorage.removeItem(key);
        const rawKey = key.replace(this.localStorageKey(''), '');
        this.cacheTags?.removeKey(rawKey);
      }
    });
  }

  private clearLocalStorage(): void {
    const prefix = this.localStorageKey('');
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  private localStorageKey(key: string): string {
    return `wtg-cache:${key}`;
  }
}
